import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteJob as deleteJobApi,
  getApplicantInterview,
  getJobApplicants,
  getMyJobs,
  updateJob as updateJobApi,
} from "../api/services/employerJobs";
import { isUbDistrict } from "../data/locations";

/**
 * The employer's own ads and their applicants, for "Миний зар".
 *
 * Ads arrive as DiilEmployerJobDto; adaptPost flattens them into the field names the
 * page renders, so the view keeps one shape whatever the API calls things.
 */

const EMPLOYER_JOBS_KEY = ["employerJobs"];

/**
 * Everything a newly published ad makes out of date.
 *
 * Publishing writes one row on the server and nothing on this device, so without this the
 * ad simply was not there afterwards: the app's default `staleTime` is five minutes and the
 * cache is persisted (see lib/queryPersist), so "Миний зарууд" kept serving the list it had
 * fetched before the wizard opened — and a reload rehydrated that same list from storage,
 * still inside its five minutes, without asking the server. The ad appeared some minutes
 * later, which read as "нийтлэгдэхгүй байна".
 *
 * Key prefixes, so every page/filter variant of a list goes at once — React Query matches a
 * partial key. `employerIdentity` is in here because the first ad is also what gives an
 * employer their company.
 */
const PUBLISH_INVALIDATES = [
  ["employerJobs"],
  ["employerIdentity"],
  ["jobs"],
  ["infiniteJobs"],
  ["jobsHomeMeta"],
  ["companies"],
];

/**
 * Drop what a publish changed. Call after the server confirms an ad, from wherever the
 * wizard was opened.
 *
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 */
export const invalidateAfterPublish = (queryClient) => {
  PUBLISH_INVALIDATES.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
};

/** How many interview lookups may be in flight at once. See useApplicantInterviews. */
const INTERVIEW_FETCH_CONCURRENCY = 8;

/**
 * Ad status as the employer should read it.
 *
 * Ads publish immediately, so APPROVED is the normal state. REJECTED means the
 * platform has taken a live ad down — the reason comes back in `approvalNote`.
 * PENDING only appears on ads created before publishing became direct.
 */
export const POST_STATUS = {
  PENDING: { label: "Хянагдаж байна", tone: "pending" },
  APPROVED: { label: "Нийтлэгдсэн", tone: "approved" },
  REJECTED: { label: "Хаагдсан", tone: "rejected" },
};

/**
 * Must carry EVERY field the edit payload resends. A PUT replaces the whole ad, so a
 * field dropped here comes back as undefined and fails the server's validation —
 * which is what made editing an address 400 before workSchedule was mapped.
 */
const adaptPost = (dto) => ({
  id: dto.id,
  title: dto.title,
  role: dto.job,
  district: dto.district || "",
  address: dto.address || "",
  region: isUbDistrict(dto.district) ? "Улаанбаатар" : "Орон нутаг",
  coordinates: dto.coordinates || null,
  salary: dto.salaryText || "",
  salaryNegotiable: !!dto.isSalaryNegotiable,
  workSchedule: dto.workSchedule || null,
  experienceLevel: dto.experienceLevel || null,
  contactPhone: dto.contactPhone || "",
  companyName: dto.companyName || "",
  // Not editable from this screen yet, but a PUT replaces the whole ad — dropped here,
  // they'd come back undefined and get wiped out by the very next unrelated edit.
  introductionText: dto.introductionText || null,
  requirementText: dto.requirementText || null,
  description: dto.description || null,
  approvalStatus: dto.approvalStatus,
  approvalNote: dto.approvalNote,
  isPosted: !!dto.isPosted,
  applicantCount: dto.applicantCount ?? 0,
  // Times the ad was opened, all time — one per person per day (JobViewService.recordView).
  viewCount: dto.viewCount ?? 0,
  // The page sorts and keys by this; the API's createdDate is "yyyy-MM-dd HH:mm:ss".
  createdAt: dto.createdDate,
  jobGroupId: dto.jobGroupId ?? null,
  jobTitleId: dto.jobTitleId ?? null,
  industryId: dto.industryId ?? null,
  // Kept so useEmployerCompany can recover the employer's company from an ad when
  // /diil/employer/me answers without a companyId.
  companyId: dto.companyId ?? null,
});

/**
 * @param {{enabled?: boolean}} [options] Pass `enabled: false` to hold the request —
 *   callers that render before the viewer is known to be a signed-in employer use this
 *   so the authenticated endpoint is never hit speculatively.
 */
export const useEmployerJobs = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: EMPLOYER_JOBS_KEY,
    enabled,
    queryFn: async () => {
      const res = await getMyJobs();
      if (!res.success) throw new Error(res.error || "Зар татахад алдаа гарлаа");
      return (Array.isArray(res.data) ? res.data : []).map(adaptPost);
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: EMPLOYER_JOBS_KEY });

  /**
   * Editing sends an ad back for review, so the server's new status is written
   * straight into the cache rather than assumed.
   */
  const updatePost = async (id, payload) => {
    const res = await updateJobApi(id, payload);
    if (!res.success) return res;

    queryClient.setQueryData(EMPLOYER_JOBS_KEY, (prev) =>
      (prev || []).map((p) => (p.id === id ? adaptPost(res.data) : p))
    );
    return res;
  };

  const removePost = async (id) => {
    const res = await deleteJobApi(id);
    if (!res.success) return res;

    queryClient.setQueryData(EMPLOYER_JOBS_KEY, (prev) =>
      (prev || []).filter((p) => p.id !== id)
    );
    return res;
  };

  return {
    posts: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refresh,
    updatePost,
    removePost,
  };
};

/**
 * Every applicant across every one of the employer's ads — as a flat list, plus the
 * per-post breakdown for callers that need it. Backs the aggregate counts on the
 * employer profile (CV received, interviews done).
 *
 * One request per ad, reusing the same cache key {@link useJobApplicants} does, so if
 * a specific ad is opened afterwards its applicants are already there.
 *
 * @param {Array<{id: number}>} posts the employer's ads
 * @returns {{applicants: Array, isLoading: boolean}}
 */
export const useEmployerAllApplicants = (posts) => {
  const ids = (posts || []).map((p) => p.id).filter(Boolean);
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["employerJobApplicants", id],
      queryFn: async () => {
        const res = await getJobApplicants(id);
        if (!res.success) throw new Error(res.error || "Горилогч татахад алдаа гарлаа");
        return Array.isArray(res.data) ? res.data : [];
      },
    })),
  });
  const applicants = results.flatMap((r) => r.data || []);
  const isLoading = results.some((r) => r.isLoading);
  return { applicants, isLoading };
};

/** Applicants on one ad. Only fetched while that ad is open. */
export const useJobApplicants = (jobId) =>
  useQuery({
    queryKey: ["employerJobApplicants", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const res = await getJobApplicants(jobId);
      if (!res.success) throw new Error(res.error || "Горилогч татахад алдаа гарлаа");
      return Array.isArray(res.data) ? res.data : [];
    },
  });

/**
 * The AI interview results for a list of applicants, keyed by applicant id.
 *
 * One request per applicant — the endpoint takes a single assessment_player id and there
 * is no batch form of it — so they are run together here rather than from each row: a
 * score sits on the row itself, and a per-row query would make the list appear a line at
 * a time. Capped at {@link INTERVIEW_FETCH_CONCURRENCY} in flight, because an ad with
 * forty applicants should not open forty sockets at once.
 *
 * Applicants who never interviewed are simply absent from the map.
 *
 * @param {Array<{id: number}>} applicants rows from {@link useJobApplicants}
 * @returns {{interviews: Object<number, Object>, isLoading: boolean}}
 */
export const useApplicantInterviews = (applicants) => {
  // Sorted so the same set of applicants is one cache entry however the list is ordered.
  const ids = (applicants || []).map((a) => a.id).filter(Boolean).sort((a, b) => a - b);

  const query = useQuery({
    queryKey: ["employerApplicantInterviews", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const results = {};
      for (let i = 0; i < ids.length; i += INTERVIEW_FETCH_CONCURRENCY) {
        const batch = ids.slice(i, i + INTERVIEW_FETCH_CONCURRENCY);
        const fetched = await Promise.all(batch.map((id) => getApplicantInterview(id)));
        batch.forEach((id, index) => {
          if (fetched[index]) results[id] = fetched[index];
        });
      }
      return results;
    },
  });

  return { interviews: query.data || {}, isLoading: query.isLoading && ids.length > 0 };
};

export default useEmployerJobs;
